import { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Plus, Pencil, Lock, Unlock, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", username: "", name: "", password: "", role_ids: [], company_ids: [] });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [u, r, c] = await Promise.all([api.get("/admin/users"), api.get("/admin/roles"), api.get("/admin/companies")]);
      setUsers(u.data.items || []);
      setRoles(r.data || []);
      setCompanies(c.data || []);
    } catch { /* ignore */ }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ email: "", username: "", name: "", password: "", role_ids: [], company_ids: [] });
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({ email: user.email, username: user.username || "", name: user.name, password: "", role_ids: user.role_ids || [], company_ids: user.company_ids || [] });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        const payload = { email: form.email, username: form.username, name: form.name, role_ids: form.role_ids, company_ids: form.company_ids };
        await api.put(`/admin/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/admin/users", form);
        toast.success("User created");
      }
      setShowModal(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const toggleLock = async (userId) => {
    try { await api.post(`/admin/users/${userId}/lock`); fetchAll(); } catch { toast.error("Failed"); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/admin/users/${userId}`); toast.success("User deleted"); fetchAll(); } catch { toast.error("Failed"); }
  };

  const toggleRole = (roleId) => {
    setForm((f) => ({
      ...f,
      role_ids: f.role_ids.includes(roleId) ? f.role_ids.filter((id) => id !== roleId) : [...f.role_ids, roleId],
    }));
  };

  const toggleCompany = (companyId) => {
    setForm((f) => ({
      ...f,
      company_ids: f.company_ids.includes(companyId) ? f.company_ids.filter((id) => id !== companyId) : [...f.company_ids, companyId],
    }));
  };

  return (
    <div data-testid="admin-users-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>User Management</h1>
          <p className="text-sm text-gray-500 mt-1">{users.length} users</p>
        </div>
        <Button data-testid="create-user-button" onClick={openCreate} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm">
          <Plus size={16} className="mr-1.5" /> New User
        </Button>
      </div>

      <Card className="border border-gray-200 shadow-sm rounded-sm">
        <CardContent className="p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-500 border-b">
                <th className="text-left py-3 px-3">Name</th>
                <th className="text-left py-3 px-3">Email</th>
                <th className="text-left py-3 px-3">Roles</th>
                <th className="text-left py-3 px-3">Companies</th>
                <th className="text-left py-3 px-3">Status</th>
                <th className="text-right py-3 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-3 font-medium">{u.name}</td>
                  <td className="py-3 px-3 text-gray-600">{u.email}</td>
                  <td className="py-3 px-3">{u.roles?.map((r) => (<Badge key={r} variant="secondary" className="mr-1 text-[10px] rounded-sm">{r}</Badge>))}</td>
                  <td className="py-3 px-3 text-xs text-gray-500">{u.companies?.map((c) => c.code).join(", ")}</td>
                  <td className="py-3 px-3">
                    {u.is_locked ? <Badge variant="destructive" className="text-[10px] rounded-sm">Locked</Badge> : u.is_active ? <Badge className="bg-green-100 text-green-800 text-[10px] rounded-sm">Active</Badge> : <Badge variant="secondary" className="text-[10px] rounded-sm">Inactive</Badge>}
                  </td>
                  <td className="py-3 px-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="h-7 w-7 p-0"><Pencil size={13} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => toggleLock(u.id)} className="h-7 w-7 p-0">{u.is_locked ? <Unlock size={13} /> : <Lock size={13} />}</Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteUser(u.id)} className="h-7 w-7 p-0 text-red-500"><Trash2 size={13} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: "Chivo, sans-serif" }}>{editing ? "Edit User" : "Create User"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-gray-500">Name</Label><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-gray-500">Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-gray-500">Email</Label><Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
            {!editing && <div><Label className="text-xs uppercase tracking-wider text-gray-500">Password</Label><Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-sm h-9 mt-1" /></div>}
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Roles</Label>
              <div className="flex flex-wrap gap-3">{roles.map((r) => (
                <label key={r.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <Checkbox checked={form.role_ids.includes(r.id)} onCheckedChange={() => toggleRole(r.id)} /> {r.name}
                </label>
              ))}</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Companies</Label>
              <div className="flex flex-wrap gap-3">{companies.map((c) => (
                <label key={c.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <Checkbox checked={form.company_ids.includes(c.id)} onCheckedChange={() => toggleCompany(c.id)} /> {c.name}
                </label>
              ))}</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-user-button" onClick={handleSave} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
